// FooterLinks.jsx
import React, { useMemo } from "react";
import styled from "styled-components";
import { TRADE_LOCATION_CONFIG } from "../config/tradeLocationConfig";

/* -------- styles (match ColumnLink look) -------- */
const Section = styled.section`
  margin-top: 8px;
`;
const Heading = styled.h4`
  font-size: 16px;
  font-weight: bold;
  margin: 8px 0;
  color: #0f172a;
`;
const Details = styled.details`
  position: relative;          /* anchor for the pop-up */
  margin: 4px 0;
  font-size: 14px;
  color: #333;

  /* hide popover by default; show when <details open> */
  & > div[data-popover] { display: none; }
  &[open] > div[data-popover] { display: block; }
`;
const Summary = styled.summary`
  list-style: none;
  cursor: pointer;
  user-select: none;
  padding: 4px 0;
  font-weight: normal;
  color: #333;
  &::-webkit-details-marker { display: none; }
  &:hover { text-decoration: underline; }
`;
const DropUp = styled.div`
  position: absolute;
  bottom: 100%;                /* pop UP instead of down */
  left: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.08);
  padding: 10px 12px;
  margin-bottom: 6px;
  z-index: 30;
  min-width: 220px;
  max-height: 50vh;
  overflow: auto;              /* scroll if tall */
`;
const City = styled.details`
  margin: 2px 0;
  font-size: 14px;

  & > div { display: none; }
  &[open] > div { display: block; }
`;
const CitySummary = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 4px 0;
  font-weight: normal;
  &::-webkit-details-marker { display: none; }
  &:hover { text-decoration: underline; }
`;
const TradeList = styled.div`
  padding: 4px 0 6px 8px;
  column-width: 160px;
  column-gap: 16px;
`;
const LinkItem = styled.a`
  display: block;
  font-size: 14px;
  color: #333;
  text-decoration: none;
  margin: 2px 0;
  break-inside: avoid;
  &:hover { text-decoration: underline; }
`;

/* -------- helpers -------- */
const slug = s => s.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/(^-|-$)/g,"");

function useCityIndex() {
  return useMemo(() => {
    const cityMap = new Map();
    Object.entries(TRADE_LOCATION_CONFIG).forEach(([tradeKey, cfg]) => {
      const tradeLabel = cfg?.label || tradeKey;
      Object.values(cfg?.cities || {}).forEach(c => {
        const cityLabel = c.label;
        const cityKey = slug(cityLabel);
        if (!cityMap.has(cityKey)) cityMap.set(cityKey, { cityLabel, trades: [] });
        cityMap.get(cityKey).trades.push({ tradeKey, tradeLabel });
      });
    });
    const arr = Array.from(cityMap.entries()).map(([cityKey, { cityLabel, trades }]) => ({
      cityKey,
      cityLabel,
      trades: trades.sort((a,b) => a.tradeLabel.localeCompare(b.tradeLabel)),
    }));
    arr.sort((a,b) => a.cityLabel.localeCompare(b.cityLabel));
    return arr;
  }, []);
}

/* -------- component -------- */
export default function FooterLinks({ maxCities = 12 }) {
  const cityIndex = useCityIndex();
  const citiesToShow = cityIndex.slice(0, maxCities);

  return (
    <Section aria-label="Browse by city then trade">
      <Heading>Browse by city</Heading>

      {/* Top-level trigger */}
      <Details>
        <Summary>Cities</Summary>

        {/* This must be a DIRECT child of the <details> above */}
        <DropUp data-popover>
          {citiesToShow.map(({ cityKey, cityLabel, trades }) => (
            <City key={cityKey}>
              <CitySummary>{cityLabel}</CitySummary>
              <TradeList>
                {trades.map(({ tradeKey, tradeLabel }) => (
                  <LinkItem
                    key={tradeKey}
                    href={`/services/${tradeKey}-${cityKey}`}
                    title={`${tradeLabel.replace(/s$/,'')} in ${cityLabel}`}
                  >
                    {tradeLabel.replace(/s$/,'')}
                  </LinkItem>
                ))}
              </TradeList>
            </City>
          ))}

          <LinkItem href="/site-map" style={{ marginTop: 6 }}>
            View all cities →
          </LinkItem>
        </DropUp>
      </Details>
    </Section>
  );
}
